<script lang="ts" setup>
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import { ScalarIcon } from '@scalar/components/icon'
import { ScalarMarkdown } from '@scalar/components/markdown'
import type { WorkspaceEventBus } from '@scalar/workspace-store/events'
import { pushDynamicScope } from '@scalar/workspace-store/helpers/dynamic-ref'
import { resolve } from '@scalar/workspace-store/resolve'
import type {
  DiscriminatorObject,
  SchemaObject,
} from '@scalar/workspace-store/schemas/v3.1/strict/openapi-document'
import { computed, inject, provide } from 'vue'

import type { SchemaOptions } from '@/components/Content/Schema/types'
import ScreenReader from '@/components/ScreenReader.vue'
import { useLocalization } from '@/features/localization'
import { scrollTargetId } from '@/helpers/lazy-bus'

import {
  resolveDynamicSchema,
  SCHEMA_DYNAMIC_SCOPE_SYMBOL,
  useDynamicScope,
} from './helpers/dynamic-scope'
import { inferDiscriminatorMappingComposition } from './helpers/get-compositions-to-render'
import { isEmptySchemaObject } from './helpers/is-empty-schema-object'
import { isTypeObject } from './helpers/is-type-object'
import { mergeAllOfSchemas } from './helpers/merge-all-of-schemas'
import { SCHEMA_ANCESTORS_SYMBOL } from './helpers/schema-cycle'
import SchemaComposition from './SchemaComposition.vue'
import SchemaHeading from './SchemaHeading.vue'
import SchemaObjectProperties from './SchemaObjectProperties.vue'
import SchemaProperty from './SchemaProperty.vue'

const {
  schema,
  level = 0,
  name,
  compact,
  noncollapsible = false,
  hideHeading,
  hideDescription = false,
  additionalProperties,
  discriminator,
  breadcrumb,
  hideModelNames = false,
  options,
  schemaContext,
  compositionPath,
  cycleKey,
} = defineProps<{
  schema?: SchemaObject
  /** Track how deep we've gone */
  level?: number
  /* Show as a heading */
  name?: string
  /** A tighter layout with less borders and without a heading */
  compact?: boolean
  /** Shows a toggle to hide/show children */
  noncollapsible?: boolean
  /** Hide the heading */
  hideHeading?: boolean
  /** Hide the schema description */
  hideDescription?: boolean
  /** Show a special one way toggle for additional properties, also has a top border when open */
  additionalProperties?: boolean
  /** Hide model names in type display */
  hideModelNames?: boolean
  /** Discriminator object */
  discriminator?: DiscriminatorObject
  /** Breadcrumb for the schema */
  breadcrumb?: string[]
  /** Event bus emitting actions */
  eventBus: WorkspaceEventBus | null
  /** Move the options into a single prop so they are easy to pass around */
  options: SchemaOptions
  /** When "requestBody", composition dropdown selection is synced with the example snippet */
  schemaContext?: string
  /** Internal path used to sync nested request body compositions with the code sample */
  compositionPath?: string[]
  /**
   * Stable identity of this schema node, derived from its raw (unresolved)
   * value by the parent. Used to detect self-referential cycles. See
   * {@link getCycleKey}.
   */
  cycleKey?: unknown
}>()
const { translate } = useLocalization()

/**
 * The dynamic scope inherited from ancestor schema resources.
 *
 * Used to bind JSON Schema 2020-12 `$dynamicRef`s to the active `$dynamicAnchor` while walking the
 * tree. Empty at the root. See {@link useDynamicScope}.
 */
const dynamicScope = useDynamicScope()

/**
 * The schema this node actually renders.
 *
 * Two normalizations happen here, both no-ops for ordinary schemas:
 * - A top-level `$dynamicRef` is bound to its concrete type via the inherited dynamic scope.
 * - A resource that extends a template through a root `$ref` (JSON Schema 2020-12 `$ref` alongside
 *   `$defs`, e.g. a `PaginatedResponse` binding) is merged so its inherited properties render.
 */
const resolvedSchema = computed((): SchemaObject | undefined => {
  if (!schema || typeof schema !== 'object') {
    return schema
  }

  const bound = resolveDynamicSchema(schema, dynamicScope)
  return '$ref' in bound ? resolve.schema(bound) : bound
})

/**
 * Re-provide the dynamic scope grown with this resource so nested `$dynamicRef`s bind here.
 *
 * Built once at setup from the resource's stable identity (like the ancestor set below);
 * `pushDynamicScope` only grows the scope for schemas that can carry a `$dynamicAnchor`.
 *
 * The raw schema is pushed, not the merged {@link resolvedSchema}: merging through `resolve.schema`
 * coerces the node and drops the resolved `$ref-value` from entries inside `$defs`, which
 * `$dynamicAnchor` resolution relies on to dereference the bound type (e.g. `User`).
 */
const scopeSchema = schema
  ? resolveDynamicSchema(schema, dynamicScope)
  : undefined
provide(
  SCHEMA_DYNAMIC_SCOPE_SYMBOL,
  scopeSchema ? pushDynamicScope(dynamicScope, scopeSchema) : dynamicScope,
)

/**
 * Cycle-safe `expandAllSchemaProperties`.
 *
 * We track ancestor schema keys along the current render path. A node is
 * treated as cyclic when its key is already present in the ancestor set, which
 * indicates that rendering has looped back onto a self-referential schema.
 *
 * This lets us default-expand finite branches while stopping automatic
 * expansion only at cycle boundaries, preventing infinite recursion.
 */
const ancestors = inject(SCHEMA_ANCESTORS_SYMBOL, undefined)

const isCyclic = computed(
  (): boolean => cycleKey != null && !!ancestors?.has(cycleKey),
)

// Re-provide the ancestor set augmented with this node so descendants can
// detect cycles back to it. Built once at setup; a node's key is stable.
const childAncestors = new Set<unknown>(ancestors ?? [])
if (cycleKey != null) {
  childAncestors.add(cycleKey)
}
provide(SCHEMA_ANCESTORS_SYMBOL, childAncestors)

const shouldForceExpand = computed(
  (): boolean => !!options.expandAllSchemaProperties && !isCyclic.value,
)

/**
 * Determines whether to show the collapse/expand toggle button.
 * We hide the toggle for non-collapsible schemas and root-level schemas.
 */
const shouldShowToggle = computed((): boolean => !noncollapsible && level > 0)

/**
 * Whether this schema sits on the path to the current anchor/scroll target.
 *
 * Property anchors are dot-joined breadcrumbs, so every disclosure that wraps
 * the target has a breadcrumb that is a prefix of the target id. Opening those
 * disclosures is what makes deep links to collapsed (hidden) properties work
 * without forcing every schema open via `expandAllSchemaProperties`.
 */
const isOnScrollTargetPath = computed((): boolean => {
  if (!breadcrumb?.length) {
    return false
  }
  const path = breadcrumb.join('.')
  const target = scrollTargetId.value
  return target === path || target.startsWith(`${path}.`)
})

/**
 * Whether the disclosure starts expanded. Non-collapsible schemas are always
 * open. When `expandAllSchemaProperties` is enabled, finite branches start
 * expanded by default while cyclic branches remain collapsed to avoid recursion
 * loops. We also open any disclosure on the path to the current scroll target so
 * deep links resolve even when the property is collapsed.
 *
 * Note: the Disclosure only reads this at mount, so it expands collapsed
 * properties on a fresh navigation (the schema mounts after the target is set),
 * not when the target changes for an already-mounted disclosure.
 */
const defaultOpen = computed(
  (): boolean =>
    noncollapsible || shouldForceExpand.value || isOnScrollTargetPath.value,
)

const childAttributesLabel = computed(
  (): string => schema?.title ?? translate('schema.childAttributes'),
)

/** Gets the description to show for the schema */
const schemaDescription = computed(() => {
  const value = resolvedSchema.value

  if (hideDescription) {
    return null
  }

  // For the request body we want to show the description of the merged allOf schema.
  // Merging keeps the base description (when set) and otherwise lets the last allOf
  // member win, matching how the merged composition is rendered below. The nested
  // merged Schema in `SchemaComposition` hides its own description in this case so
  // the text is not rendered twice.
  if (schema?.allOf && schema.allOf.length > 0 && name === 'Request Body') {
    return mergeAllOfSchemas(schema)?.description || null
  }

  // Don't show description if there's no description or it's not a string
  if (!value?.description || typeof value.description !== 'string') {
    return null
  }

  // Don't show description for enum schemas (they have special handling)
  if (value.enum) {
    return null
  }

  // Will be shown in the properties anyway. A composed schema is the exception:
  // its members render as their own cards with their own descriptions, so the
  // schema's own description has nowhere else to go.
  if (
    !('properties' in value) &&
    !('patternProperties' in value) &&
    !('additionalProperties' in value) &&
    !('allOf' in value)
  ) {
    return null
  }

  // Return the schema's own description
  return value.description
})

/**
 * Infer a selector for mapped discriminators that do not declare `oneOf`.
 * Threaded discriminators skip inference to avoid recursive allOf variants.
 */
const inferredDiscriminatorComposition = computed(() =>
  schema && !discriminator && isTypeObject(schema)
    ? inferDiscriminatorMappingComposition(schema, options.document)
    : null,
)

// Prevent click action if noncollapsible
const handleClick = (e: MouseEvent) => {
  if (noncollapsible) {
    e.stopPropagation()
  }
}
</script>
<template>
  <Disclosure
    v-if="resolvedSchema && Object.keys(resolvedSchema).length"
    v-slot="{ open }"
    :defaultOpen="defaultOpen">
    <div
      class="schema-card"
      :class="[
        `schema-card--level-${level}`,
        { 'schema-card--compact': compact, 'schema-card--open': open },
        { 'border-t': additionalProperties && open },
      ]">
      <!-- Schema description -->
      <div
        v-if="schemaDescription"
        class="schema-card-description">
        <ScalarMarkdown :value="schemaDescription" />
      </div>
      <div
        v-if="isEmptySchemaObject(resolvedSchema)"
        class="pt-2">
        {{ translate('schema.emptyObject') }}
      </div>
      <div
        class="schema-properties"
        :class="{
          'schema-properties-open': open,
        }">
        <!-- Toggle to collapse/expand long lists of properties -->
        <div
          v-if="additionalProperties"
          v-show="!open"
          class="schema-properties">
          <DisclosureButton
            as="button"
            class="schema-card-title schema-card-title--compact"
            @click.capture="handleClick">
            <ScalarIcon
              class="schema-card-title-icon"
              icon="Add"
              size="sm" />
            {{ translate('schema.showAdditionalProperties') }}
            <ScreenReader v-if="name">
              {{ translate('schema.forName', { name }) }}
            </ScreenReader>
          </DisclosureButton>
        </div>

        <DisclosureButton
          v-else-if="shouldShowToggle"
          v-show="!hideHeading && !(noncollapsible && compact)"
          :as="noncollapsible ? 'div' : 'button'"
          class="schema-card-title"
          :class="{ 'schema-card-title--compact': compact }"
          :style="{
            top: `calc(var(--refs-viewport-offset) +  calc(var(--schema-title-height) * ${level}))`,
          }"
          @click.capture="handleClick">
          <template v-if="compact">
            <ScalarIcon
              class="schema-card-title-icon"
              :class="{ 'schema-card-title-icon--open': open }"
              icon="Add"
              size="sm" />
            <template v-if="open">
              {{
                translate('schema.hideChildAttributes', {
                  name: childAttributesLabel,
                })
              }}
            </template>
            <template v-else>
              {{
                translate('schema.showChildAttributes', {
                  name: childAttributesLabel,
                })
              }}
            </template>
            <ScreenReader v-if="name">
              {{ translate('schema.forName', { name }) }}
            </ScreenReader>
          </template>
          <template v-else>
            <ScalarIcon
              class="schema-card-title-icon"
              :class="{ 'schema-card-title-icon--open': open }"
              icon="Add"
              size="sm" />
            <SchemaHeading
              :name="resolvedSchema?.title ?? name"
              :value="resolvedSchema" />
          </template>
        </DisclosureButton>
        <DisclosurePanel
          v-if="!additionalProperties || open"
          as="ul"
          :static="!shouldShowToggle">
          <!-- Variant selector inferred from a discriminator mapping -->
          <SchemaComposition
            v-if="inferredDiscriminatorComposition"
            :breadcrumb
            :compact
            composition="oneOf"
            :compositionPath="compositionPath"
            :discriminator="schema?.discriminator"
            :eventBus="eventBus"
            :hideHeading
            :hideModelNames
            :level="level"
            :name="name"
            :options
            :schema="inferredDiscriminatorComposition"
            :schemaContext="schemaContext" />
          <!-- Object properties -->
          <SchemaObjectProperties
            v-else-if="isTypeObject(resolvedSchema)"
            :breadcrumb
            :compact
            :compositionPath="compositionPath"
            :discriminator
            :eventBus="eventBus"
            :hideHeading
            :hideModelNames
            :level="level + 1"
            :options
            :schema="resolvedSchema"
            :schemaContext="schemaContext" />
          <!-- Not an object -->
          <template v-else>
            <SchemaProperty
              v-if="resolvedSchema"
              :breadcrumb
              :compact
              :compositionPath="compositionPath"
              :discriminator
              :eventBus="eventBus"
              :hideHeading
              :hideModelNames
              :level
              :options
              :schema="resolvedSchema"
              :schemaContext="schemaContext" />
          </template>
        </DisclosurePanel>
      </div>
    </div>
  </Disclosure>
</template>
<style scoped>
.error {
  background-color: var(--scalar-color-red);
}
.schema-card {
  font-size: var(--scalar-font-size-4);
  color: var(--scalar-color-1);
}
.schema-card-title {
  height: var(--schema-title-height);

  padding: 6px 8px;

  display: flex;
  align-items: center;
  gap: 4px;

  color: var(--scalar-color-2);
  font-weight: var(--scalar-semibold);
  font-size: var(--scalar-mini);
  border-bottom: var(--scalar-border-width) solid transparent;
}
button.schema-card-title {
  cursor: pointer;
}
button.schema-card-title:hover {
  color: var(--scalar-color-1);
}
.schema-card-title-icon--open {
  transform: rotate(45deg);
}
.schema-properties-open > .schema-card-title {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-bottom: var(--scalar-border-width) solid var(--scalar-border-color);
}
.schema-properties-open > .schema-properties {
  width: fit-content;
}
.schema-card-description {
  color: var(--scalar-color-2);
}
.schema-card-description + .schema-properties {
  width: fit-content;
}
.schema-card-description + .schema-properties {
  margin-top: 8px;
}
.schema-card--level-0:nth-of-type(1)
  > .schema-card-description:has(+ .schema-properties) {
  margin-bottom: -8px;
  padding-bottom: 8px;
  border-bottom: var(--scalar-border-width) solid var(--scalar-border-color);
}
.schema-card--level-0
  ~ .schema-card--level-0
  > .schema-card-description:has(+ .schema-properties) {
  padding-top: 8px;
}

.schema-properties-open.schema-properties,
.schema-properties-open > .schema-card--open {
  width: 100%;
}
.schema-properties {
  display: flex;
  flex-direction: column;

  border: var(--scalar-border-width) solid var(--scalar-border-color);
  border-radius: var(--scalar-radius-lg);
  width: fit-content;
}
.schema-properties-name {
  width: 100%;
}
.schema-properties .schema-properties {
  border-radius: 13.5px;
}
.schema-properties .schema-properties.schema-properties-open {
  border-radius: var(--scalar-radius-lg);
}
.schema-properties-open {
  width: 100%;
}
.schema-card--compact {
  align-self: flex-start;
}
.schema-card--compact.schema-card--open {
  align-self: initial;
}
.schema-card-title--compact {
  color: var(--scalar-color-2);
  padding: 6px 10px 6px 8px;
  height: auto;
  border-bottom: none;
}
.schema-card-title--compact > .schema-card-title-icon {
  margin: 0;
}
.schema-card-title--compact > .schema-card-title-icon--open {
  transform: rotate(45deg);
}
.schema-properties-open > .schema-card-title--compact {
  position: static;
}
.property--level-0
  > .schema-properties
  > .schema-card--level-0
  > .schema-properties {
  border: none;
}
.property--level-0
  .schema-card--level-0:not(.schema-card--compact)
  .property--level-1 {
  padding: 0 0 8px;
}
:not(.composition-panel)
  > .schema-card--compact.schema-card--level-0
  > .schema-properties {
  border: none;
}
:deep(.schema-card-description) p {
  font-size: var(--scalar-small, var(--scalar-paragraph));
  color: var(--scalar-color-2);
  margin-bottom: 0;
  display: block;
  margin-bottom: 6px;
}
.children .schema-card-description:first-of-type {
  padding-top: 0;
}
</style>
